/**
 * AutoToolMatchModal Component
 *
 * Shows tools whose names were found in the prompt text.
 * Since these are exact name matches, confidence is high — all pre-selected.
 */

import React from 'react';
import { Modal } from '../../../components/common';
import { useI18n } from '@/i18n';
import type { ToolMatchResult } from '../hooks/useAutoToolMatch';

// ============================================================================
// Sub-components
// ============================================================================

const MatchTypeBadge: React.FC<{ matchType: ToolMatchResult['matchType']; isZh: boolean }> = ({ matchType, isZh }) => {
  const config = {
    exact_name: { label: isZh ? '工具名匹配' : 'Name Match', className: 'bg-green-100 text-green-700' },
    display_name: { label: isZh ? '显示名匹配' : 'Display Name', className: 'bg-blue-100 text-blue-700' },
    sub_tool_name: { label: isZh ? '子工具匹配' : 'Sub-tool Match', className: 'bg-teal-100 text-teal-700' },
  };
  const { label, className } = config[matchType];
  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${className}`}>{label}</span>
  );
};

const TypeBadge: React.FC<{ type: 'custom' | 'builtin' | 'mcp'; isZh: boolean }> = ({ type, isZh }) => {
  const config = {
    custom: { label: isZh ? '自定义' : 'Custom', className: 'bg-green-100 text-green-800' },
    builtin: { label: isZh ? '内置' : 'Builtin', className: 'bg-blue-100 text-blue-800' },
    mcp: { label: 'MCP', className: 'bg-purple-100 text-purple-800' },
  };
  const { label, className } = config[type];
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>
  );
};

// ============================================================================
// Tool Match Item
// ============================================================================

interface ToolMatchItemProps {
  result: ToolMatchResult;
  isSelected: boolean;
  isZh: boolean;
  onToggle: (toolId: string) => void;
}

const ToolMatchItem: React.FC<ToolMatchItemProps> = ({ result, isSelected, isZh, onToggle }) => {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
        isSelected
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      onClick={() => onToggle(result.toolId)}
    >
      <div className="mt-0.5 flex-shrink-0">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(result.toolId)}
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-gray-900 text-sm truncate">
            {result.displayName}
          </span>
          <TypeBadge type={result.type} isZh={isZh} />
          <MatchTypeBadge matchType={result.matchType} isZh={isZh} />
        </div>

        {result.displayName !== result.toolId && (
          <div className="text-xs text-gray-400 mb-1 font-mono truncate">
            {result.toolId}
          </div>
        )}

        <p className="text-xs text-gray-600 line-clamp-2 mb-1.5">
          {result.description}
        </p>

        {/* Matched terms from prompt */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-gray-400">{isZh ? '在提示词中找到:' : 'Found in prompt:'}</span>
          {result.matchedTerms.map((term, i) => (
            <code
              key={i}
              className="inline-block px-1.5 py-0.5 bg-green-50 text-green-800 text-xs rounded border border-green-200 font-mono"
            >
              {term}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Modal
// ============================================================================

export interface AutoToolMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: ToolMatchResult[];
  selectedTools: Set<string>;
  isLoading: boolean;
  onToggleSelection: (toolId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onConfirm: () => void;
}

export const AutoToolMatchModal: React.FC<AutoToolMatchModalProps> = ({
  isOpen,
  onClose,
  results,
  selectedTools,
  isLoading,
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
  onConfirm,
}) => {
  const { language } = useI18n();
  const isZh = language === 'zh';

  const customResults = results.filter(r => r.type === 'custom');
  const builtinResults = results.filter(r => r.type === 'builtin');
  const mcpResults = results.filter(r => r.type === 'mcp');

  const selectedCount = selectedTools.size;
  const totalCount = results.length;

  const handleConfirmClick = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isZh ? '自动工具匹配' : 'Auto Tool Match'}
      size="4xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {isZh
            ? '在提示词中扫描到以下工具名称。取消勾选不需要的工具，然后点击确认添加。'
            : 'The following tool names were found in the prompt. Uncheck any you don\'t need, then confirm.'}
        </p>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <span className="ml-3 text-sm text-gray-600">
              {isZh ? '正在扫描提示词中的工具名...' : 'Scanning prompt for tool names...'}
            </span>
          </div>
        )}

        {!isLoading && (
          <>
            {/* Stats */}
            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">
                  {isZh
                    ? `找到 ${totalCount} 个工具`
                    : `Found ${totalCount} tools`}
                </span>
                {selectedCount > 0 && (
                  <span className="text-blue-600 font-medium">
                    {isZh ? `已选择 ${selectedCount} 个` : `${selectedCount} selected`}
                  </span>
                )}
              </div>
              {totalCount > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={onSelectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {isZh ? '全选' : 'Select All'}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={onDeselectAll}
                    className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {isZh ? '取消全选' : 'Deselect All'}
                  </button>
                </div>
              )}
            </div>

            {/* No results */}
            {totalCount === 0 && (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm">
                  {isZh
                    ? '提示词中未找到已注册的工具名称。请确认提示词中包含了工具名（如 calculate_natal_chart_by_id），或手动浏览工具列表添加。'
                    : 'No registered tool names found in the prompt. Make sure tool names are mentioned in the prompt, or browse tools manually.'}
                </p>
              </div>
            )}

            {/* Results */}
            {totalCount > 0 && (
              <div className="max-h-[50vh] overflow-y-auto space-y-4">
                {customResults.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      {isZh ? '自定义工具' : 'Custom Tools'}
                      <span className="text-xs font-normal text-gray-400">({customResults.length})</span>
                    </h4>
                    <div className="space-y-2">
                      {customResults.map(r => (
                        <ToolMatchItem key={r.toolId} result={r} isSelected={selectedTools.has(r.toolId)} isZh={isZh} onToggle={onToggleSelection} />
                      ))}
                    </div>
                  </div>
                )}

                {builtinResults.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />
                      {isZh ? '内置工具' : 'Builtin Tools'}
                      <span className="text-xs font-normal text-gray-400">({builtinResults.length})</span>
                    </h4>
                    <div className="space-y-2">
                      {builtinResults.map(r => (
                        <ToolMatchItem key={r.toolId} result={r} isSelected={selectedTools.has(r.toolId)} isZh={isZh} onToggle={onToggleSelection} />
                      ))}
                    </div>
                  </div>
                )}

                {mcpResults.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      {isZh ? 'MCP 工具' : 'MCP Tools'}
                      <span className="text-xs font-normal text-gray-400">({mcpResults.length})</span>
                    </h4>
                    <div className="space-y-2">
                      {mcpResults.map(r => (
                        <ToolMatchItem key={r.toolId} result={r} isSelected={selectedTools.has(r.toolId)} isZh={isZh} onToggle={onToggleSelection} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            {isZh ? '取消' : 'Cancel'}
          </button>
          <button
            onClick={handleConfirmClick}
            disabled={selectedCount === 0}
            className={`px-4 py-2 rounded transition-colors ${
              selectedCount > 0
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {selectedCount > 0
              ? (isZh ? `确认添加 (${selectedCount})` : `Add Selected (${selectedCount})`)
              : (isZh ? '请先选择工具' : 'Select tools first')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
